import {
  PushFailed,
  querySession,
  type QueryClosed,
  type QuerySession,
  type QueryTransport
} from '@st/live-query'
import { Effect, type Scope, type Stream } from 'effect'
import type { JSONPatchOperation } from './json-patch'
import { topic as realtimeTopic, type RealtimeSocket } from './realtime'
import type { SDKRequestFor, SDKResponseFor } from './sdk'
import type { SDKCommand } from './sdk.types'
import { transformJsonPatch, transformSDKResponse } from './transform-middleware'

export type { QueryClosed } from '@st/live-query'

export type SDKQuerySession = {
  readonly query: <K extends SDKCommand['type']>(
    request: SDKRequestFor<K>
  ) => Stream.Stream<SDKResponseFor<K>, QueryClosed>
}

/**
 * A live query session over the realtime socket, typed by the generated SDK
 * types and decoded with the SDK's key casing. The mechanism is
 * `@st/live-query`; this binds it to the socket and to `SDKCommand` requests.
 */
export function makeQuerySession(
  realtime: Pick<RealtimeSocket, 'subscribe' | 'push' | 'connectedTopics' | 'rejoin'>
): Effect.Effect<SDKQuerySession, never, Scope.Scope> {
  const transport: QueryTransport = {
    subscribe: (topic, onEvent) => realtime.subscribe(realtimeTopic(topic), onEvent),
    rejoin: (topic) => realtime.rejoin(realtimeTopic(topic)),
    push: (topic, event, payload) =>
      realtime
        .push(realtimeTopic(topic), event, payload)
        .pipe(Effect.mapError((error) => new PushFailed(error.reason, error.response))),
    joinedTopics: realtime.connectedTopics
  }

  return Effect.map(
    querySession(transport, {
      decodeValue: (value) => transformSDKResponse(value),
      decodePatch: (patch) =>
        transformJsonPatch(patch as ReadonlyArray<JSONPatchOperation>) as typeof patch
    }),
    (session: QuerySession): SDKQuerySession => ({
      query: (request) => {
        const { type, ...params } = request
        return session.query(type, params)
      }
    })
  )
}
